import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useLocationStore } from "../..";
import usePropertySearch from "../../hooks/usePropertySearch";
import IProperty from "../../interfaces/IProperty";
import { formatDate, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import addLocationsStyles from "./AddLocations.module.css";
import { ISingleLocationState } from "./AddSingleLocation";

function SingleLocationDetails({ companyId, state, onPrevious, onAdd }: ISingleLocationProps) {
    const locationStore = useLocationStore();
    const { searchByAddress } = usePropertySearch();
    const isAlreadyAdded = useMemo(() => computed(() => locationStore.locations.some(l => l.pipedriveCompanyId === companyId && l.address === state.address)), [companyId, state]).get();
    const [isSearching, setIsSearching] = useState(false);
    const uploadStatus = useMemo(() => isAlreadyAdded ? "Location is already added" : isSearching ? "Searching for location..." : "Location not found", [isAlreadyAdded, isSearching]);
    const [property, setProperty] = useState<IProperty | undefined>();

    useEffect(() => {
        if (!isAlreadyAdded) {
            setIsSearching(true);
            searchByAddress(state.address!).then(property => {
                setProperty(property);
                setIsSearching(false);
            });
        }
    }, [state.address, isAlreadyAdded]);

    return (
        <>
            {property ? (
                <>
                    <div className={addLocationsStyles.detailsTitle}>Single Location Details</div>
                    <div className={addLocationsStyles.columns}>
                        <div className={addLocationsStyles.column}>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>SITE NAME</div>
                                <div className={commonStyles.value}>{state.siteName || `${property.city}, ${property.state}`}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>ADDRESS</div>
                                <div className={commonStyles.value}>{state.address}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>ZONING</div>
                                <div className={commonStyles.value}>{property.zoning}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>BUILDING SIZE</div>
                                <div className={commonStyles.value}>{!!property.buildingSize && `${formatNumber(property.buildingSize)} Sq-Ft`}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>LAND SIZE</div>
                                <div className={commonStyles.value}>{!!property.landSize && `${formatNumber(property.landSize)} Acres`}</div>
                            </div>
                        </div>
                        <div className={addLocationsStyles.column}>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>OWNER</div>
                                <div className={commonStyles.value}>{property.ownerEntity}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>DATE SOLD</div>
                                <div className={commonStyles.value}>{!!property.saleDate && formatDate(new Date(property.saleDate))}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>SALE PRICE</div>
                                <div className={commonStyles.value}>{!!property.salePrice && formatPrice(property.salePrice)}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>OWNER ADDRESS</div>
                                <div className={commonStyles.value}>{property.ownerAddress}</div>
                            </div>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>NOTES</div>
                                <div className={commonStyles.value}>{state.notes}</div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className={addLocationsStyles.detailsTitle}>{uploadStatus}</div>
            )}
            <div className={[commonStyles.modalControls, commonStyles.controlGroup].join(" ")}>
                <button className={commonStyles.secondaryButton} onClick={onPrevious}>Previous</button>
                {property && (
                    <button className={commonStyles.primaryButton} onClick={() => {
                        locationStore.addLocation({
                            pipedriveCompanyId: companyId,
                            regridId: property!.regridId,
                            siteName: state.siteName || `${property.city}, ${property.state}`,
                            address: state.address!,
                            notes: state.notes!,
                            property,
                        });

                        onAdd();
                    }}>Add</button>
                )}
            </div>
        </>
    );
}

interface ISingleLocationProps {
    companyId: number;
    state: ISingleLocationState;
    onPrevious: () => void;
    onAdd: () => void;
}

export default observer(SingleLocationDetails);